// Utils
import { BOND_TAG, COMPARISON_ITEM_TYPES } from './constants';
import { INSTRUMENT_TYPE } from '@/enums/InstrumentByCode';
import { ITEM_COLORS } from './colors';
import { PORTFOLIO_TYPES, HOLDING_TYPES } from './portfolios';
import { QUICK_SEARCH_FILTERS, QUICK_SEARCH_PILL_LABELS } from '@/enums/QuickSearch';
import { convertShelvesToShelfStatuses, ShelfStatus } from './shelf-management';
import { UNIVERSES_ACRONYMS, UNIVERSES_EN_CA } from '@/enums/Funds';
import { PERMISSIONS, userHasPermissionEnabled } from './permissions';
import { QUICK_SEARCH_REDUCER_NAME } from '@/redux/modules/quick-search';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Fund from '@/types/fund';
import FundFiltersData from '@/types/fund-filters-data';
import Nullable from '@/types/nullable';
import QuickSearchItem from '@/types/quick-search-item';
import RecentSearchInstrument from '@/types/recent-search-instrument';
import RecentSearchItem from '@/types/recent-search-item';
import User from '@/types/user';
import LocalizedString from '@/types/localized-string';
import CustomProxy from '@/types/custom-proxy';
import { formatLocalizedString, formatMessage } from './i18n';
import CUSTOM_PROXY_ITEM from '@/types/custom-proxy-item';

const getQuickSearchPortfolioType = (item: QuickSearchItem) => {
  if (item.enterprise != null) {
    return PORTFOLIO_TYPES.FIRM;
  }
  if (item.mine) {
    return PORTFOLIO_TYPES.TEMPLATE;
  }
  return PORTFOLIO_TYPES.CLIENT;
};

export const generateQuickSearchItemColor = () => ITEM_COLORS[Math.floor(Math.random() * ITEM_COLORS.length)];

export const getPillLabel = (item: QuickSearchItem) => {
  const { _id, type, universe } = item;
  const isBond = _id?.startsWith(BOND_TAG);

  if (isBond) {
    return INSTRUMENT_TYPE.BOND;
  }

  if (universe && QUICK_SEARCH_PILL_LABELS[universe]) {
    return QUICK_SEARCH_PILL_LABELS[universe];
  }

  return type;
};

export const fundToQuickSearchItem = (fund: Fund, saved: boolean = true): QuickSearchItem => {
  const { universe } = fund;

  return {
    _id: fund._id,
    type: HOLDING_TYPES.FUND,
    name: fund.names?.full as LocalizedString,
    symbol: fund.symbols && fund.symbols[0],
    saved,
    flags: fund.flags,
    complianceComparisons: fund.complianceComparisons,
    filteredShelves: fund.filteredShelves,
    color: generateQuickSearchItemColor(),
    universe: mapUniverseToAcronym(universe),
  };
};

/**
 * Convert a fund universe label to a standardized acronym.
 *
 * @param universe - The fund universe as a `LocalizedString` (optional).
 * @returns A `UNIVERSES_ACRONYMS` value when recognized; otherwise `undefined`.
 */
export const mapUniverseToAcronym = (universe?: LocalizedString): UNIVERSES_ACRONYMS | undefined => {
  const label = universe?.['en-CA'];
  switch (label) {
    case UNIVERSES_EN_CA.INDICES:
      return UNIVERSES_ACRONYMS.INDICES;
    case UNIVERSES_EN_CA.SEPARATELY_MANAGED_ACCOUNTS:
      return UNIVERSES_ACRONYMS.SMA;
    case UNIVERSES_EN_CA.EXCHANGE_TRADED_FUNDS:
      return UNIVERSES_ACRONYMS.ETF;
    default:
      return undefined;
  }
};

export const benchmarkToQuickSearchItem = (benchmark: ComparisonBenchmark, saved: boolean = true): QuickSearchItem => ({
  name: benchmark.name,
  symbol: benchmark.ticker,
  saved,
  _id: benchmark._id,
  type: HOLDING_TYPES.BENCHMARK,
  color: generateQuickSearchItemColor(),
});

export const mapApiResultsToQuickSearchItems = (results: QuickSearchItem[]): QuickSearchItem[] =>
  results.map(r => {
    switch (r.type) {
      case COMPARISON_ITEM_TYPES.FUND: {
        return {
          ...r,
          name: r.name,
          code: r.symbol,
          color: generateQuickSearchItemColor(),
          enterprise: r.enterprise,
        };
      }
      case COMPARISON_ITEM_TYPES.PORTFOLIO: {
        return {
          ...r,
          color: generateQuickSearchItemColor(),
          enterprise: r.enterprise,
          portfolioType: getQuickSearchPortfolioType(r),
        };
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        return {
          ...r,
          code: r.symbol,
          color: generateQuickSearchItemColor(),
        };
      }
      case COMPARISON_ITEM_TYPES.BENCHMARK: {
        return {
          ...r,
          code: r.symbol,
        };
      }
      default: {
        return r;
      }
    }
  });

export const mapApiRecentSearchToRecentSearchItems = (recentSearch: RecentSearchInstrument[]): RecentSearchItem[] =>
  // TODO: update type when the data will be defined on the BE
  recentSearch.reduce((acc: RecentSearchItem[], cur: RecentSearchInstrument) => {
    if (cur?.instrumentType) {
      switch (cur.instrumentType) {
        case INSTRUMENT_TYPE.FUND:
        case INSTRUMENT_TYPE.STOCK: {
          acc.push({
            ...cur,
            name: cur.names,
            type: cur.instrumentType,
            refId: cur.instrumentId,
            flags: cur.onShelf ? [ShelfStatus.ON_SHELF] : [],
          });
          break;
        }
      }
    }

    return acc;
  }, []);

export const filterIsShown = (
  filter: QUICK_SEARCH_FILTERS,
  user?: Nullable<User>,
  fundFilters?: FundFiltersData,
  targetReducer?: QUICK_SEARCH_REDUCER_NAME,
  hiddenTabs?: QUICK_SEARCH_FILTERS[],
) => {
  const isComparisonReducer = targetReducer === QUICK_SEARCH_REDUCER_NAME.COMPARISON;

  if (isComparisonReducer && filter === QUICK_SEARCH_FILTERS.SIMULATION) {
    return false;
  }

  if (hiddenTabs && hiddenTabs.includes(filter)) {
    return false;
  }

  const allowedBenchmarkFilter = isComparisonReducer ? QUICK_SEARCH_FILTERS.BENCHMARKS : QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES;
  const alwaysShown = [QUICK_SEARCH_FILTERS.ALL, QUICK_SEARCH_FILTERS.FUNDS, QUICK_SEARCH_FILTERS.SIMULATION, QUICK_SEARCH_FILTERS.FAVORITE];
  const filtersByPermission = [
    { filter: QUICK_SEARCH_FILTERS.STOCKS, permission: PERMISSIONS.STOCKS },
    { filter: allowedBenchmarkFilter, permission: PERMISSIONS.BENCHMARKS },
    { filter: QUICK_SEARCH_FILTERS.BONDS, permission: PERMISSIONS.BONDS },
    { filter: QUICK_SEARCH_FILTERS.ALTS, permission: PERMISSIONS.PREQIN_ALT },
    { filter: QUICK_SEARCH_FILTERS.PROXIES, permission: PERMISSIONS.CUSTOM_PROXIES },
  ];

  if (alwaysShown.includes(filter)) {
    return true;
  }

  if (!user) {
    return false;
  }

  const permissionFilter = filtersByPermission.find(fp => fp.filter === filter);
  if (permissionFilter) {
    return userHasPermissionEnabled(user, permissionFilter.permission);
  }

  if (fundFilters && filter === QUICK_SEARCH_FILTERS.SMA) {
    return universeIsInFundFilters(UNIVERSES_EN_CA.SEPARATELY_MANAGED_ACCOUNTS, fundFilters);
  }

  return false;
};

export const universeIsInFundFilters = (universe: string, fundsFilters: FundFiltersData) => {
  const universes: string[] = fundsFilters && fundsFilters.universes;
  return universes && universes.map(u => u.toLowerCase()).includes(universe.toLowerCase());
};

export const transformCustomProxyToQuickSearchItem = (item: CustomProxy): CUSTOM_PROXY_ITEM => {
  const { id, names, details, type } = item;
  const nameObj = names?.full || names?.short || {};

  const isProxyRef = ((proxyRefDetails: any): proxyRefDetails is { proxyRef: any } => !!proxyRefDetails?.proxyRef)(details);

  return {
    lineItemAddLabel: formatMessage('Search.QuickSearchV2.AddToPortfolio'),
    id: isProxyRef ? details.proxyRef?.proxyId : details.proxyKey || id,
    isProxy: true,
    name: formatLocalizedString(nameObj),
    type: isProxyRef ? details.proxyRef?.type : undefined,
    code: null, // Always null for proxies as if undefined, it shows dashed line in UI
    pillLabel: INSTRUMENT_TYPE.PROXY,
    proxyType: type,
    filteredShelves: details.shelves,
    flags: convertShelvesToShelfStatuses(details.shelves, []),
    ...(!isProxyRef
      ? {
          options: { ...details },
        }
      : {}),
  };
};
